import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { T, card, cardTitle, tbl, th, td, bandTokens } from '../lib/theme'

const fmt  = (n) => '₦' + (Number(n)||0).toLocaleString(undefined,{maximumFractionDigits:0})
const fmtM = (n) => '₦' + ((Number(n)||0)/1000000).toLocaleString(undefined,{maximumFractionDigits:1}) + 'M'

export default function Dashboard() {
  const [agents,      setAgents]      = useState([])
  const [saScores,    setSaScores]    = useState([])   // super_agent_scores
  const [weekFunding, setWeekFunding] = useState(0)    // total wallet funding this week
  const [season,      setSeason]      = useState('dry')
  const [latestWeek,  setLatestWeek]  = useState('—')
  const [loading,     setLoading]     = useState(true)

  useEffect(() => {
    async function load() {
      const [
        { data:ag },
        { data:sc },
        { data:se },
      ] = await Promise.all([
        supabase.from('agents').select('id,full_name,status,agent_class,lga_id,super_agent_id,lgas(name,zone,tier),super_agents(company_name)'),
        supabase.from('super_agent_scores').select('*, super_agents(company_name,lgas(name))').order('week_ending',{ascending:false}).limit(200),
        supabase.from('app_settings').select('*'),
      ])

      setAgents(ag||[])
      setSaScores(sc||[])

      // Derive latestWeek from wallet_funding (most recent upload) — scores may lag
      const { data:wfWeek } = await supabase.from('wallet_funding')
        .select('week_ending').order('week_ending',{ascending:false}).limit(1)
      const latest = (wfWeek && wfWeek[0]?.week_ending) || (sc && sc[0]?.week_ending) || '—'
      setLatestWeek(latest)

      if (latest !== '—') {
        // Sum wallet_funding exactly as Target Board does:
        // build agentFundingMap then sum only agents that exist in the agents table
        const { data:wf } = await supabase.from('wallet_funding')
          .select('agent_id,agent_id_text,amount_funded').eq('week_ending', latest)
        const agentFundingMap = {}
        ;(wf||[]).forEach(r => {
          const key = r.agent_id || r.agent_id_text
          if (!key) return
          agentFundingMap[key] = (agentFundingMap[key]||0) + (Number(r.amount_funded)||0)
        })
        // only sum agents that exist in the agents table (same as Target Board boardRows)
        const total = (ag||[]).reduce((s,a) => {
          return s + (agentFundingMap[a.id] || agentFundingMap[a.agent_id] || 0)
        }, 0)
        setWeekFunding(total)
      }
      if (se) { const s=se.find(x=>x.key==='season'); if(s) setSeason(s.value) }
      setLoading(false)
    }
    load()
  }, [])

  const activeAgents  = agents.filter(a => a.status==='Active').length
  const weekScores    = saScores.filter(s => s.week_ending === latestWeek)
  const metCount      = weekScores.filter(s => s.target_met).length
  const totalTarget   = weekScores.reduce((s,r) => s+(r.weekly_target||0), 0)
  const overallPct    = totalTarget > 0 ? Math.round(weekFunding/totalTarget*100) : 0

  // Top 5 super agents
  const top5 = [...weekScores].sort((a,b) => (b.funding_pct||0)-(a.funding_pct||0)).slice(0,5)

  // Super agents below target
  const atRisk = weekScores.filter(s => !s.target_met)
    .sort((a,b) => (a.funding_pct||0)-(b.funding_pct||0)).slice(0,5)

  if (loading) return <div style={{ color:T.txtSecondary, fontSize:13 }}>Loading dashboard…</div>

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:20, fontWeight:800, color:T.txtPrimary, letterSpacing:'-.01em', marginBottom:4 }}>
          Dashboard
        </div>
        <div style={{ fontSize:12, color:T.txtSecondary }}>
          Katsina State Informal Sector Collections · Agent Compliance Overview
        </div>
      </div>

      {/* Season badge */}
      <div style={{ display:'inline-flex', alignItems:'center', gap:8,
        background: season==='dry'?'rgba(245,166,35,.1)':'rgba(30,144,255,.1)',
        border: season==='dry'?'1px solid rgba(245,166,35,.25)':'1px solid rgba(30,144,255,.25)',
        borderRadius:20, padding:'5px 14px', marginBottom:24, fontSize:11, fontWeight:700,
        color: season==='dry'?T.amber:T.blue }}>
        {season==='dry' ? '☀ Dry Season (Oct–Apr) · Full Targets' : '🌧 Rainy Season · −25% Adjustment Active'}
      </div>

      {/* KPI row — 6 tiles */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:12 }}>
        {[
          { label:'Total Agents',         value: agents.length,          sub:'registered',                colour:T.blue  },
          { label:'Active Agents',         value: activeAgents,           sub:'currently deployed',         colour:T.blue  },
          { label:'Super Agents Scored',   value: weekScores.length,      sub:`week ${latestWeek}`,         colour:T.txtPrimary },
        ].map(({ label,value,sub,colour }) => (
          <div key={label} style={{ background:T.surface, borderRadius:10, border:`1px solid ${T.border}`, padding:'16px 20px' }}>
            <div style={{ fontSize:10, fontWeight:600, color:T.txtMuted, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:8 }}>{label}</div>
            <div style={{ fontSize:26, fontWeight:800, color:colour, lineHeight:1.1, marginBottom:4 }}>{value}</div>
            <div style={{ fontSize:10, color:T.txtMuted }}>{sub}</div>
          </div>
        ))}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
        {[
          { label:'Total Wallet Funding',  value: fmtM(weekFunding),      sub:`this week (${latestWeek})`,  colour:'#3fb950' },
          { label:'Targets Met',           value: `${metCount} / ${weekScores.length}`, sub:'super agents on target', colour: metCount===weekScores.length&&weekScores.length>0?'#3fb950':'#f0883e' },
          { label:'Overall vs Target',     value: overallPct + '%',        sub: overallPct>=100?'On track':'Below target', colour: overallPct>=100?'#3fb950':'#f85149' },
        ].map(({ label,value,sub,colour }) => (
          <div key={label} style={{ background:T.surface, borderRadius:10, border:`1px solid ${T.border}`, padding:'16px 20px' }}>
            <div style={{ fontSize:10, fontWeight:600, color:T.txtMuted, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:8 }}>{label}</div>
            <div style={{ fontSize:26, fontWeight:800, color:colour, lineHeight:1.1, marginBottom:4 }}>{value}</div>
            <div style={{ fontSize:10, color:T.txtMuted }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Two panels */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>

        {/* Top 5 super agents */}
        <div style={card}>
          <div style={cardTitle}>Top 5 Super Agents — This Week</div>
          <table style={tbl}>
            <thead><tr>
              {['#','Super Agent','LGA','Funded','%','Status'].map(h => <th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {top5.map((r,i) => (
                <tr key={r.id} style={{ background: i===0?'rgba(30,144,255,.04)':'transparent' }}>
                  <td style={{ ...td, fontWeight:800, color:i<3?T.amber:T.txtMuted, width:28 }}>{i+1}</td>
                  <td style={{ ...td, fontWeight:600 }}>{r.super_agents?.company_name}</td>
                  <td style={{ ...td, color:T.txtSecondary, fontSize:11 }}>{r.super_agents?.lgas?.name||'—'}</td>
                  <td style={{ ...td, fontWeight:700, color:'#3fb950' }}>{fmt(r.total_funded)}</td>
                  <td style={{ ...td, fontWeight:800, color:r.target_met?'#3fb950':'#f85149' }}>{r.funding_pct}%</td>
                  <td style={td}>
                    <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:20, fontSize:9, fontWeight:700,
                      background: r.target_met?'rgba(63,185,80,.15)':'rgba(248,81,73,.12)',
                      color:       r.target_met?'#3fb950':'#f85149',
                      border:      r.target_met?'1px solid rgba(63,185,80,.3)':'1px solid rgba(248,81,73,.25)' }}>
                      {r.target_met ? 'MET' : 'BELOW'}
                    </span>
                  </td>
                </tr>
              ))}
              {top5.length===0 && <tr><td colSpan={6} style={{ ...td, textAlign:'center', color:T.txtMuted }}>No scores yet this week</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Needs attention */}
        <div style={card}>
          <div style={cardTitle}>⚠ Below Target — Needs Attention</div>
          {atRisk.length===0 ? (
            <div style={{ fontSize:12, color:'#3fb950', fontWeight:600, padding:'12px 0' }}>
              All Super Agents have met their targets this week.
            </div>
          ) : (
            <table style={tbl}>
              <thead><tr>
                {['Super Agent','LGA','Target','Funded','Shortfall','%'].map(h => <th key={h} style={th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {atRisk.map(r => (
                  <tr key={r.id}>
                    <td style={{ ...td, fontWeight:600 }}>{r.super_agents?.company_name}</td>
                    <td style={{ ...td, color:T.txtSecondary, fontSize:11 }}>{r.super_agents?.lgas?.name||'—'}</td>
                    <td style={{ ...td, color:T.txtSecondary }}>{fmt(r.weekly_target)}</td>
                    <td style={{ ...td, color:'#f85149', fontWeight:700 }}>{fmt(r.total_funded)}</td>
                    <td style={{ ...td, color:'#f85149', fontWeight:700 }}>{fmt((r.weekly_target||0)-(r.total_funded||0))}</td>
                    <td style={{ ...td, fontWeight:800, color:'#f85149' }}>{r.funding_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
